import Vue from 'vue'
import store from '@/store'
import BleConnectionManager from '@/ble/connectionManager'
import { BleProviderEventTypes } from '@/ble/constants'
import { EQUIPPED_CHECK_COUNT, LOW_BATTERY_BORDER } from '@/constants/ble'
import {
  isEcgServiceHeartRateValues,
  isEcgServiceBatteryValues
} from '@/types/utils'
import {
  BleDeviceTypes,
  DEFAULT_ECG_TERM_HR,
  PR_RESET_INTERVAL,
  ECG_SAVE_SECONDS
} from '@/constants/ble'
import { BLEDevice } from '@/ble/types'
import { BLEConfig } from '@/types/appSettings'
import { NC1BLEConfigParams } from '@/types/ble'
import VitalsignSettingFetcher from '@/ble/vitalsignSettingFetcher'
import { VitalsignPayload } from '@/api/vitalsign/types'
import { AlertPayload } from '@/api/alert/types'
import dayjs from 'dayjs'
import { ReceivedValues } from '@/store/ble/types'

const createPayload = (
  values: Partial<ReceivedValues> | undefined = undefined
) => {
  const receivedValues = values || store.getters['ble/receivedValues']

  const data = {
    heartRate: receivedValues.heartRate,
    pulseRate: receivedValues.pulseRate,
    temperature: receivedValues.temperature,
    weight: receivedValues.weight,
    spo2: receivedValues.spo2,
    glucose: receivedValues.glucose,
    sbp: receivedValues.systolicPressure,
    dbp: receivedValues.diastolicPressure
  }

  return (Object.keys(data) as Array<keyof VitalsignPayload>).reduce(
    (payload, key) => {
      if ((data as VitalsignPayload)[key] === null) {
        return payload
      }

      return Object.assign(payload, {
        [key]: (data as VitalsignPayload)[key]
      })
    },
    {} as VitalsignPayload
  )
}

const sendVitalsign = (patientId: number, heartRate: number) => {
  const payload = createPayload()

  payload.heartRate = heartRate
  payload.measureDatetime = dayjs().format('YYYY-MM-DDTHH:mm:ssZ')

  Vue.$api.vitalsign.create({
    patientId,
    payload
  })
}

const sendValues = (
  patientId: number,
  values: Partial<ReceivedValues>,
  measureDateTime?: Date
) => {
  const payload = createPayload(values)
  const dt = dayjs(measureDateTime) || dayjs()
  payload.measureDatetime = dt.format('YYYY-MM-DDTHH:mm:ssZ')

  Vue.$api.vitalsign.create({
    patientId,
    payload
  })
}

const sendAlert = (
  patientId: number,
  alertType: string,
  alertLevel: number,
  alertFrom: string,
  reason: string
) => {
  const payload: AlertPayload = {
    alertType: alertType,
    alertLevel: alertLevel,
    alertFrom: alertFrom,
    reason: reason
  }

  Vue.$api.alert.create({
    patientId,
    payload
  })
}

const overLimit = (
  heartRate: number,
  isLimitchk: boolean,
  limitLow: number | null,
  limitHigh: number | null
) => {
  if (!isLimitchk) {
    return false
  }

  if (
    (limitLow === null || heartRate >= limitLow) &&
    (limitHigh === null || heartRate <= limitHigh)
  ) {
    return false
  }

  return true
}

export const watchVitalsign = async (patientId: number) => {
  const provider = BleConnectionManager.getProvider()
  let equippedValues: Array<boolean> = []
  let equipStatus = true
  let prResetTimer: number | undefined
  let waitingPrAutoSend = false
  let lastPrAutoSendAt: number | undefined

  provider.addListener(BleProviderEventTypes.RECEIVE_VALUE, async readVal => {
    if (store.getters['ble/autoSending']) {
      switch (readVal.deviceType) {
        case BleDeviceTypes.ELECTRONIC_BLOOD_PRESSURE_MONITOR:
        case BleDeviceTypes.MEDICAL_BLOOD_PRESSURE_MONITOR:
          sendValues(patientId, {
            systolicPressure: readVal.systolicPressure,
            diastolicPressure: readVal.diastolicPressure,
            pulseRate: readVal.pr
          })
          break
        case BleDeviceTypes.ELECTRONIC_THERMOMETER:
        case BleDeviceTypes.NONCONTACT_THERMOMETER:
          sendValues(patientId, {
            temperature: readVal.temperature
          })
          break
        case BleDeviceTypes.BLOOD_SUGAR_LEVEL_MEASUREMENT:
          sendValues(
            patientId,
            {
              glucose: readVal.glucose
            },
            readVal.measurementTime
          )
          break
        case BleDeviceTypes.SPO2_MONITOR_MIGHTY_SAT:
        case BleDeviceTypes.SPO2_MONITOR_TM1121:
          sendValues(patientId, {
            spo2: readVal.spo2,
            pulseRate: readVal.pr
          })
          break
        case BleDeviceTypes.ECG_SERVICE:
          if (isEcgServiceHeartRateValues(readVal)) {
            sendValues(patientId, {
              heartRate: readVal.heartRate
            })
          }
          break
        case BleDeviceTypes.BODY_COMPOSITION_MONITOR:
          sendValues(patientId, {
            weight: readVal.weight
          })
          break
      }
    }
  })

  provider.addListener(
    BleProviderEventTypes.RECEIVE_ECG_SERVICE,
    async value => {
      if (isEcgServiceBatteryValues(value)) {
        const beforeBattery = store.state.ecg.battery
        if (beforeBattery === -1 || beforeBattery > value.battery) {
          store.commit('ecg/setBattery', value.battery)
          if (
            (beforeBattery === -1 || beforeBattery >= LOW_BATTERY_BORDER) &&
            LOW_BATTERY_BORDER > value.battery
          ) {
            sendAlert(patientId, 'ble', 1, '6', 'low_battery')
          }
          return
        }
      }

      if (!isEcgServiceHeartRateValues(value)) {
        return
      }

      if (prResetTimer) {
        clearTimeout(prResetTimer)
      }
      prResetTimer = setTimeout(() => {
        store.commit('ecg/setPr', -1)
      }, PR_RESET_INTERVAL)

      if (value.equipped !== undefined) {
        equippedValues.push(value.equipped)
        if (equippedValues.length > EQUIPPED_CHECK_COUNT) {
          const isEquipped = equippedValues.every(value => {
            return value === true
          })
          const isNotEquipped = equippedValues.every(value => {
            return value === false
          })
          const beforeEquipStatus = equipStatus
          if (isEquipped) {
            equippedValues = []
            equipStatus = true
          } else if (isNotEquipped) {
            equippedValues = []
            equipStatus = false
            if (!equipStatus && beforeEquipStatus) {
              sendAlert(patientId, 'ble', 1, '6', 'no_equipped')
            }
          } else {
            equippedValues.shift()
          }
        }
      }

      const {
        emgIsLimitchkHeartRate,
        emgLimitHeartRateLow,
        emgLimitHeartRateHigh,
        warnIsLimitchkHeartRate,
        warnLimitHeartRateLow,
        warnLimitHeartRateHigh
      } = await VitalsignSettingFetcher.getSettings(patientId)

      if (
        overLimit(
          value.heartRate,
          emgIsLimitchkHeartRate,
          emgLimitHeartRateLow,
          emgLimitHeartRateHigh
        )
      ) {
        if (!store.getters['ble/autoSending']) {
          sendVitalsign(patientId, value.heartRate)
        }

        const device = provider.getDeviceByDeviceType(
          BleDeviceTypes.ECG_SERVICE
        ) as BLEDevice

        const config = store.getters['appSettings/bleConfig'](
          device.address
        ) as BLEConfig<BleDeviceTypes.ECG_SERVICE>

        if (config && config.config.constantECG === true) {
          if (!waitingPrAutoSend) {
            const now = dayjs().unix()
            if (
              !lastPrAutoSendAt ||
              lastPrAutoSendAt < now - ECG_SAVE_SECONDS
            ) {
              store.dispatch('ecg/save', { patientId })
              lastPrAutoSendAt = now
            }
          }
          return
        }

        const newConfig: NC1BLEConfigParams = config
          ? { ...config.config, constantECG: true }
          : { termHR: DEFAULT_ECG_TERM_HR, constantECG: true }

        setTimeout(() => {
          store.dispatch('ecg/save', { patientId })
          waitingPrAutoSend = false
        }, ECG_SAVE_SECONDS * 1000)
        waitingPrAutoSend = true

        await provider.changeNC1BLEConfig(device.address, newConfig)

        await store.dispatch('appSettings/updateBleConfig', {
          [device.address]: {
            deviceType: device.deviceType,
            deviceName: device.deviceName,
            address: device.address,
            config: newConfig
          }
        })

        return
      }

      if (
        !store.getters['ble/autoSending'] &&
        overLimit(
          value.heartRate,
          warnIsLimitchkHeartRate,
          warnLimitHeartRateLow,
          warnLimitHeartRateHigh
        )
      ) {
        sendVitalsign(patientId, value.heartRate)
      }
    }
  )
}
